import React, { useEffect, useState } from "react";
import TableHead from "@/components/tables/TableHead";
import UsersTable from "@/components/tables/UsersTable";
import { fetchAllGroups } from "@/services/group.service";
import { fetchAllRoles } from "@/services/role.service";
import { fetchUsers } from "@/services/user.service";
import { options } from "@/utils/constants/constants";
import { useDataQueries } from "@/utils/hooks/useDataQueries";
import { getListCacheBootstrap, writeListCache } from "@/utils/listPageCache";

const UserList = () => {
    const [pageNo, setPageNo, dataLength, setDataLength, sorting, setSorting, filter, setFilter, searchItem, setSearchItem, resetDataQueries] = useDataQueries();
    const listBootstrap = getListCacheBootstrap("users", { dataLength, sorting, searchItem });

    const [isLoading, setIsLoading] = useState(!listBootstrap.hasCache);
    const [users, setUsers] = useState<any>(listBootstrap.items);
    const [usersLeft, setUsersLeft] = useState(true);
    const [usersHistory, setUsersHistory] = useState<any>(listBootstrap.items);
    const [allFetched, setAllFetched] = useState(false);
    const [groups, setGroups] = React.useState([]);
    const [roles, setRoles] = React.useState([]);
    const [dataLoad, setDataLoad] = useState(!listBootstrap.hasCache);
    const [totalCount, setTotalCount] = useState(listBootstrap.totalCount);

    useEffect(() => {
        fetchAllRoles(
            (response: any) => {
                setRoles(response.data.roles);
            },
            (error: any) => {
                console.log(error);
            }
        );
        fetchAllGroups(
            (response: any) => {
                setGroups(response.data.groups);
            },
            (error: any) => {
                console.log(error);
            }
        );
    }, []);

    useEffect(() => {
        const { cacheKey, cached } = getListCacheBootstrap("users", { dataLength, sorting, searchItem });

        setPageNo(1);
        setUsersLeft(true);
        setAllFetched(false);
        if (cached) {
            setUsers(cached.items);
            setUsersHistory(cached.items);
            setTotalCount(cached.totalCount);
            setDataLoad(false);
            setIsLoading(false);
        } else {
            setIsLoading(true);
            setDataLoad(true);
        }

        fetchUsers(
            1,
            dataLength,
            sorting,
            filter,
            searchItem,
            (response: any) => {
                const nextUsers = response.data.users ?? [];
                writeListCache(cacheKey, nextUsers, response.data.total_count);
                setIsLoading(false);
                setDataLoad(false);
                setTotalCount(response.data.total_count);
                if (response.data?.users?.length < dataLength) {
                    setUsersLeft(false);
                    setAllFetched(true);
                }
                setUsers(nextUsers);
                setUsersHistory(nextUsers);
            },
            (error: any) => {
                console.log(error);
                setIsLoading(false);
                setDataLoad(false);
            }
        );
    }, [dataLength, sorting, searchItem]);

    useEffect(() => {
        const handleScroll = () => {
            if (allFetched || isLoading || !usersLeft) {
                return;
            }

            if (
                window.innerHeight + document.documentElement.scrollTop <
                document.documentElement.scrollHeight - 1
            ) {
                return;
            }

            const nextPage = pageNo + 1;
            setPageNo(nextPage);
            setIsLoading(true);
            fetchUsers(
                nextPage,
                dataLength,
                sorting,
                filter,
                searchItem,
                (response: any) => {
                    setIsLoading(false);
                    setTotalCount(response.data.total_count);
                    if (response.data?.users?.length < dataLength) {
                        setUsersLeft(false);
                        setAllFetched(true);
                    }
                    const r_users = [...users, ...response.data.users];
                    setUsersHistory(r_users);
                    setUsers(r_users);
                },
                (error: any) => {
                    console.log(error);
                    setIsLoading(false);
                }
            );
        };

        window.addEventListener("scroll", handleScroll, { passive: true });
        return () => window.removeEventListener("scroll", handleScroll);
    }, [
        allFetched,
        isLoading,
        usersLeft,
        pageNo,
        users,
        dataLength,
        sorting,
        filter,
        searchItem,
        setPageNo,
    ]);

    const resetResponse = () => {
        setUsersLeft(true);
        setAllFetched(false);
    };

    const reset = () => {
        resetDataQueries?.();
        resetResponse();
    };

    const search = (searchText: string) => {
        setSearchItem(searchText);
    };

    const handleApplyFilter = () => {
        setPageNo(1);
        resetResponse();
        setDataLoad(true);
        setIsLoading(true);
        fetchUsers(
            1,
            dataLength,
            sorting,
            filter,
            searchItem,
            (response: any) => {
                setDataLoad(false);
                setIsLoading(false);
                setTotalCount(response.data.total_count);
                if (response.data?.users?.length < dataLength) {
                    setUsersLeft(false);
                    setAllFetched(true);
                }
                const users = response.data.users;
                setUsers(users);
                setUsersHistory(users);
            },
            (error: any) => {
                console.log(error);
                setDataLoad(false);
                setIsLoading(false);
            }
        );
    };

    return (
        <div className="gs-list-page-card">
            <div className="gs-list-page-card-header">
                <TableHead
                    id="user-table"
                    handleReset={resetResponse}
                    pageNo={pageNo as number}
                    dataLength={dataLength as number}
                    groups={groups}
                    roles={roles}
                    setFilter={setFilter}
                    filter={filter}
                    setDataLength={setDataLength as (dataLength: number) => void}
                    reset={reset}
                    search={search}
                    title="Users"
                    options={options}
                    handleApplyFilter={handleApplyFilter}
                    setPageNo={setPageNo}
                    currentEnvo={users.length}
                    totalEnvo={totalCount}
                />
            </div>
            <div>
                <UsersTable
                    is_allow_manage_shared_access={true}
                    id="user-table"
                    reset={reset}
                    setUsers={setUsers}
                    search={search}
                    isLoading={isLoading}
                    dataLength={dataLength as number}
                    sorting={sorting}
                    setSorting={setSorting as (sorting: any) => void}
                    title="Users"
                    searchItem={searchItem as string}
                    allFetched={false}
                    users={users}
                    setPageNo={setPageNo}
                    dataload={dataLoad}
                    resetDataQueries={resetDataQueries}
                />
            </div>
        </div>
    );
};

export default UserList;
